/**
 * Option Reference Normalization
 * Centralized normalization for option inputs (symbol or 4-arg form)
 */

import { parseOccSymbol, buildOccOptionSymbol } from "../symbols/occOptionSymbol";
import { normalizeUnderlying, normalizeSide } from "../symbols/occOptionSymbol";
import { coerceExcelDateToYyyyMmDd, coerceExcelNumber } from "../excel/normalize";

export type OptionRight = "C" | "P";

export type OptionRef = {
  contractSymbol: string;
  underlying: string;
  right: OptionRight;
  expiration: string; // YYYY-MM-DD format
  strike: number;
};

/**
 * Parsed Option Reference (for entity functions)
 * Simplified structure for option entity parsing
 */
export interface ParsedOptionRef {
  underlying: string;      // Uppercase
  expiration: string;      // YYYY-MM-DD
  strike: number;          // Numeric (keep decimals)
  right: "C" | "P";
  occ?: string;            // OCC symbol if available
}

/**
 * Convert YYMMDD to YYYY-MM-DD format
 * Uses same year windowing as coerceExcelDateToYyyyMmDd: 00-79 → 2000-2079, 80-99 → 1980-1999
 */
function convertYYMMDDToYYYYMMDD(yymmdd: string): string {
  if (!/^\d{6}$/.test(yymmdd)) {
    throw new Error("Invalid YYMMDD format: " + yymmdd);
  }

  const yy = yymmdd.slice(0, 2);
  const mm = yymmdd.slice(2, 4);
  const dd = yymmdd.slice(4, 6);

  // Year windowing: 00-79 → 2000-2079, 80-99 → 1980-1999
  const yearNum = parseInt(yy, 10);
  let yyyy: string;
  if (yearNum >= 0 && yearNum <= 79) {
    yyyy = "20" + yy;
  } else if (yearNum >= 80 && yearNum <= 99) {
    yyyy = "19" + yy;
  } else {
    throw new Error("Invalid year in YYMMDD: " + yy);
  }

  return yyyy + "-" + mm + "-" + dd;
}

/**
 * Normalize option input to canonical OptionRef
 * Accepts either:
 * - 1 argument: contractSymbol (string)
 * - 4 arguments: underlying, right, expiration, strike
 *
 * Returns normalized OptionRef with all fields canonicalized
 */
export function toOptionRef(args: unknown[]): OptionRef {
  // Arity detection (strict, with defensive undefined-slot handling)
  const is1Arg =
    args.length === 1 ||
    (args.length >= 4 &&
      typeof args[0] === "string" &&
      args.slice(1).every((v) => v === undefined));

  if (is1Arg) {
    // 1-arg path: contractSymbol
    const contractSymbol = args[0];
    if (typeof contractSymbol !== "string") {
      throw new Error("Contract symbol must be a string");
    }
    const trimmed = contractSymbol.trim();
    if (trimmed === "") {
      throw new Error("Contract symbol cannot be empty");
    }

    // Parse the symbol
    let parsed;
    try {
      parsed = parseOccSymbol(trimmed);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Invalid symbol";
      throw new Error("Contract symbol is malformed. Expected OCC option symbol. " + msg);
    }

    // Convert YYMMDD to YYYY-MM-DD
    const expiration = convertYYMMDDToYYYYMMDD(parsed.expirationYYMMDD);

    // Rebuild canonical symbol using normalized fields
    const rebuilt = buildOccOptionSymbol({
      underlying: parsed.root,
      side: parsed.side,
      expiration: expiration,
      strike: parsed.strike.value,
    });

    return {
      contractSymbol: rebuilt.unspaced,
      underlying: parsed.root,
      right: parsed.side,
      expiration: expiration,
      strike: parsed.strike.value,
    };
  } else if (args.length === 4) {
    // 4-arg path: underlying, right, expiration, strike
    const underlying = normalizeUnderlying(args[0]);
    const right = normalizeSide(args[1]);
    const expiration = coerceExcelDateToYyyyMmDd(args[2]);
    const strikeNum = coerceExcelNumber(args[3]);

    // Validate strike
    if (!isFinite(strikeNum)) {
      throw new Error("Strike must be a finite number");
    }
    if (strikeNum <= 0) {
      throw new Error("Strike must be greater than 0");
    }

    // Build canonical symbol
    const built = buildOccOptionSymbol({
      underlying: underlying,
      side: right,
      expiration: expiration,
      strike: strikeNum,
    });

    return {
      contractSymbol: built.unspaced,
      underlying: underlying,
      right: right,
      expiration: expiration,
      strike: strikeNum,
    };
  } else {
    throw new Error(
      "Invalid number of arguments. Expected either 1 argument (contractSymbol) or 4 arguments (underlying, right, expiration, strike)."
    );
  }
}

/**
 * Parse option reference from string input
 * Accepts OCC symbol or canonical format like "AAPL 2026-01-16 200C"
 * @param input - Option reference string
 * @returns ParsedOptionRef with normalized fields
 */
export function parseOptionRef(input: string): ParsedOptionRef {
  const trimmed = input.trim();
  if (trimmed === "") {
    throw new Error("Option reference cannot be empty");
  }

  // Try OCC symbol format first
  try {
    const parsed = parseOccSymbol(trimmed);
    const expiration = convertYYMMDDToYYYYMMDD(parsed.expirationYYMMDD);
    
    return {
      underlying: parsed.root.toUpperCase(),
      expiration: expiration,
      strike: parsed.strike.value,
      right: parsed.side,
      occ: parsed.formats.unspaced,
    };
  } catch (occError) {
    // If OCC parsing fails, try canonical format: "AAPL 2026-01-16 200C"
    const canonicalMatch = trimmed.match(/^([A-Z0-9]+)\s+(\d{4}-\d{2}-\d{2})\s+(\d+(?:\.\d+)?)([CP])$/i);
    if (canonicalMatch) {
      const [, underlying, expiration, strikeStr, rightStr] = canonicalMatch;
      const strike = parseFloat(strikeStr);
      
      if (!isFinite(strike) || strike <= 0) {
        throw new Error("Invalid strike price: " + strikeStr);
      }
      
      // Validate expiration format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(expiration)) {
        throw new Error("Invalid expiration format: " + expiration);
      }
      
      // Normalize right
      const right = rightStr.toUpperCase() === "C" ? "C" : "P";
      
      // Build OCC symbol for occ field
      let occ: string | undefined;
      try {
        const built = buildOccOptionSymbol({
          underlying: underlying.toUpperCase(),
          side: right,
          expiration: expiration,
          strike: strike,
        });
        occ = built.unspaced;
      } catch {
        // OCC build failed, leave occ undefined
      }
      
      return {
        underlying: underlying.toUpperCase(),
        expiration: expiration,
        strike: strike,
        right: right,
        occ: occ,
      };
    }
    
    // Neither format matched
    const occMsg = occError instanceof Error ? occError.message : "Invalid OCC symbol";
    throw new Error("Invalid option reference format. Expected OCC symbol or 'AAPL 2026-01-16 200C' format. " + occMsg);
  }
}

/**
 * Format option reference as display name
 * @param ref - ParsedOptionRef
 * @returns Display string like "AAPL 2026-01-16 200C"
 */
export function formatOptionDisplayName(ref: ParsedOptionRef): string {
  return ref.underlying + " " + ref.expiration + " " + ref.strike + ref.right;
}
